import { Flex } from '@chakra-ui/react';
import { faEdit, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonText, FormFeedback, useToast } from '@morressier/alembic';
import { useState } from 'react';
import { FieldLabel } from 'src/components/Field';
import { AffiliationDetail } from '../UpdateAffiliationDetails';
import {
  handleDuplicateCheck,
  IOrganizationForm,
  pickFields,
  stepTwoFormFields
} from 'src/components/UserRegistrationSteps/StepThree/signupHook';
import UpdateSecondaryAffiliationModal from './UpdateSecondaryAffliationModal';
import UpdateAffiliationDetailsModal from './UpdateAffiliationDetailsModal';
import { useFormContext } from 'react-hook-form';
import { IUserForm } from './AddUser';

const AffiliationSection = ({
  organizationId,
  configs
}: {
  organizationId: string;
  configs: any;
}) => {
  const {
    watch,
    setValue,
    formState: { errors },
    clearErrors
  } = useFormContext<IUserForm>();
  const toast = useToast();

  const affiliationDetails = watch('affiliationDetails');
  const secondaryAffiliation = watch('additionalAffiliations');

  const [isAffiliationModalOpen, setIsAffiliationModalOpen] = useState(false);
  const [isSecondaryAffiliationModalOpen, setIsSecondaryAffiliationModalOpen] =
    useState(false);

  const onAddAffiliation = (affiliationDetails: IOrganizationForm) => {
    setValue('affiliationDetails', affiliationDetails, {
      shouldValidate: false,
      shouldDirty: true
    });
    setIsAffiliationModalOpen(false);
    clearErrors('affiliationDetails');
  };

  const onAddSecondaryAffiliation = (
    additionalAffiliationDetails: IOrganizationForm[]
  ) => {
    setValue('additionalAffiliations', additionalAffiliationDetails, {
      shouldValidate: false,
      shouldDirty: true
    });
    setIsSecondaryAffiliationModalOpen(false);
  };

  const handleAffiliationDuplicateCheck = (
    additionalAffiliations: IOrganizationForm[],
    primaryAffiliation: IOrganizationForm 
  ) => {
    const updatedSecondaryAffiliations = additionalAffiliations;
    if (affiliationDetails && updatedSecondaryAffiliations?.length) {
      const filteredAffiliationDetails = pickFields(
        primaryAffiliation || {},
        stepTwoFormFields
      );
      const filteredAdditionalAffiliations =
        updatedSecondaryAffiliations?.map(a =>
          pickFields(a, stepTwoFormFields)
        ) || [];

      const lastIdx = filteredAdditionalAffiliations.length - 1;
      const selectedAffiliation = filteredAdditionalAffiliations[lastIdx];

      if (selectedAffiliation && filteredAdditionalAffiliations.length > 0) {
        const isDuplicateMatches = handleDuplicateCheck(
          selectedAffiliation,
          `additionalAffiliations.${lastIdx}`,
          filteredAdditionalAffiliations,
          filteredAffiliationDetails
        );

        if (isDuplicateMatches.length) {
          const duplicatesLabel = isDuplicateMatches
            .map(match =>
              match.index === 'primary'
                ? 'Primary affiliation'
                : `Secondary affiliation(s) ${Number(match.index) + 1}`
            )
            .join(', ');

          toast({
            variant: 'error',
            title: 'Duplicate affiliation detected',
            description: `Secondary affiliation(s) ${
              lastIdx + 1
            } is identical to ${duplicatesLabel}. Please modify one of these entries to continue.`,
            isClosable: true
          });

          return true;
        }
      }
      return false;
    }
    return false;
  };

  return (
    <>
      <Flex direction={'column'} gap={3}>
        <Flex flexDirection="column" gap={2} alignItems="flex-start">
          <Flex
            justifyContent={'space-between'}
            width={'full'}
            alignItems={'flex-start'}
          >
            <FieldLabel label="Primary Affiliation" subText isRequired />
            {affiliationDetails && affiliationDetails.organizationName && (
              <ButtonText onClick={() => setIsAffiliationModalOpen(true)}>
                <FontAwesomeIcon icon={faEdit} />
              </ButtonText>
            )}
          </Flex>
          {affiliationDetails && affiliationDetails.organizationName ? (
            <AffiliationDetail affiliation={affiliationDetails} />
          ) : (
            <ButtonText
              variant={errors.affiliationDetails?.message ? 'caution' : ''}
              onClick={() => setIsAffiliationModalOpen(true)}
            >
              <FontAwesomeIcon icon={faPlus} />
              Add Primary Affiliation
            </ButtonText>
          )}
          {errors.affiliationDetails?.message && (
            <FormFeedback variant="error">
              {errors.affiliationDetails.message}
            </FormFeedback>
          )}
        </Flex>

        <Flex direction={'column'} gap={3} alignItems="flex-start">
          <Flex
            justifyContent={'space-between'}
            width={'full'}
            alignItems={'flex-start'}
          >
            <FieldLabel
              label="Secondary Affiliation"
              isRequired={false}
              subText
            />
            {secondaryAffiliation && secondaryAffiliation?.length ? (
              <ButtonText
                onClick={() => setIsSecondaryAffiliationModalOpen(true)}
              >
                <FontAwesomeIcon icon={faEdit} />
              </ButtonText>
            ) : null}
          </Flex>
          {secondaryAffiliation && secondaryAffiliation.length ? (
            secondaryAffiliation.map(affiliation => (
              <AffiliationDetail affiliation={affiliation} />
            ))
          ) : (
            <ButtonText
              onClick={() => setIsSecondaryAffiliationModalOpen(true)}
            >
              <FontAwesomeIcon icon={faPlus} />
              Add Secondary Affiliation
            </ButtonText>
          )}
        </Flex>
      </Flex>
      {isAffiliationModalOpen && (
        <UpdateAffiliationDetailsModal
          isOpen={isAffiliationModalOpen}
          onClose={() => setIsAffiliationModalOpen(false)}
          organizationId={organizationId}
          onAdd={onAddAffiliation}
          affiliationDetails={affiliationDetails ?? null}
          additionalAffiliations={secondaryAffiliation ?? null}
          handleAffiliationDuplicateCheck={handleAffiliationDuplicateCheck}
        />
      )}
      {isSecondaryAffiliationModalOpen && (
        <UpdateSecondaryAffiliationModal
          isOpen={isSecondaryAffiliationModalOpen}
          onClose={() => setIsSecondaryAffiliationModalOpen(false)}
          configs={configs}
          onAdd={onAddSecondaryAffiliation}
          affiliationDetails={affiliationDetails ?? null}
          additionalAffiliations={secondaryAffiliation ?? null}
          handleAffiliationDuplicateCheck={handleAffiliationDuplicateCheck}
        />
      )}
    </>
  );
};

export default AffiliationSection;
